import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTheme } from '@dhl-ui/theme'
import { Box } from '@dhl-ui/components'

type Item = {
  /**
   * Unique reference for the item
   */
  id: string

  /**
   * You can not have mutliple items with same priority. The lowest priority will
   * be compacted last when there is no room for displaying the non compact version
   */
  priority: number

  /**
   * The width when normally painted of the HeaderItem
   */
  width: number

  /**
   * The compact version of the HeaderItem
   */
  widthCompact: number
}

type Context = {
  registerItem: (arg: Item) => void
  compactIndex: number
}

const HeaderLayoutContext = createContext({} as Context)

const sortDesc = (a: Item, b: Item) => a.priority - b.priority

export const HeaderLayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const { variant } = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState(0)
  const [registeredItems, setRegisteredItems] = useState<Item[]>([])
  const [compactIndex, setCompactIndex] = useState<number>(Infinity)

  // TODO: maybe gap should be static or in rem?
  // our gap is inside our consumers `gap: { xs: 1, md: 2 }` we asume they did this...
  const gap = ['mobile', 'tablet'].includes(variant) ? 8 : 16

  // TODO: maybe also deregister when not painting component conditionally?
  const registerItem = useCallback((item: Item) => {
    const { id, priority, width, widthCompact } = item

    setRegisteredItems((prev) => {
      if (prev.some((prevItem) => prevItem.id === id)) {
        return prev.map((prevItem) => (prevItem.id === id ? item : prevItem))
      }
      return [...prev, { id, priority, width, widthCompact }].sort(sortDesc)
    })
  }, [])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setAvailableWidth(entry.contentRect.width)
      })
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const { aboveOrEqual, below } = registeredItems.reduce(
      (acc, item) => {
        if (item.priority >= compactIndex) {
          acc.aboveOrEqual.push(item)
        } else {
          acc.below.push(item)
        }
        return acc
      },
      { aboveOrEqual: [], below: [] } as { aboveOrEqual: Item[]; below: Item[] }
    )

    const usedWidth = aboveOrEqual.reduce((acc, item) => acc + item.widthCompact + gap, 0)

    let availableSpace = availableWidth

    availableSpace -= usedWidth

    let remainingSpace = availableSpace

    const firstNonFittingItem = below.sort(sortDesc).find((item) => {
      if (remainingSpace >= item.width + gap) {
        remainingSpace -= item.width + gap
        return false
      }
      return true
    })

    const newCompactIndex = firstNonFittingItem?.priority

    // maybe our last compacted item can now be visible again?
    const firstItem = aboveOrEqual.find((item) => item.priority === compactIndex)

    if (!newCompactIndex && firstItem?.width && firstItem.width + gap < remainingSpace) {
      // TODO: now assumption no gaps between prios :)
      setCompactIndex(firstItem.priority + 1)
    }

    if (newCompactIndex && JSON.stringify(compactIndex) !== JSON.stringify(newCompactIndex)) {
      setCompactIndex(newCompactIndex)
    }

    // TODO: check this again
    // reason: prevent circular
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableWidth, registeredItems])

  const values = useMemo(
    () => ({
      compactIndex,
      registerItem,
    }),

    // TODO: maybe its fine to add the registration as well :)
    // reason: keeping this clean
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableWidth, compactIndex]
  )

  return (
    <HeaderLayoutContext.Provider value={values}>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        {children}
      </Box>
    </HeaderLayoutContext.Provider>
  )
}

export const useHeaderLayout = () => useContext(HeaderLayoutContext)
